import React, { useState, useRef, useCallback } from 'react';
import { UploadCloud } from 'lucide-react';

export interface FileDropZoneProps {
  onFilesSelected: (files: File[]) => void;
  accept?: string; // MIME types, comma-separated
  maxFiles?: number;
  disabled?: boolean;
}

function parseMimeTypes(accept?: string): string[] {
  if (!accept) return [];
  return accept
    .split(',')
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
}

function matchesMime(file: File, acceptedTypes: string[]): boolean {
  if (acceptedTypes.length === 0) return true;
  const fileMime = file.type.toLowerCase();
  return acceptedTypes.some((accepted) => {
    // Exact match (e.g., "application/pdf")
    if (accepted === fileMime) return true;
    // Wildcard match (e.g., "image/*")
    if (accepted.endsWith('/*')) {
      const prefix = accepted.slice(0, -2);
      return fileMime.startsWith(prefix + '/');
    }
    // Extension-based (e.g., ".csv")
    if (accepted.startsWith('.')) {
      return file.name.toLowerCase().endsWith(accepted);
    }
    return false;
  });
}

export function FileDropZone({
  onFilesSelected,
  accept,
  maxFiles,
  disabled = false,
}: FileDropZoneProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const acceptedTypes = parseMimeTypes(accept);

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || disabled) return;

      let files = Array.from(fileList);

      // Filter by MIME type if accept is provided
      if (acceptedTypes.length > 0) {
        files = files.filter((f) => matchesMime(f, acceptedTypes));
      }

      // Limit file count
      if (maxFiles && files.length > maxFiles) {
        files = files.slice(0, maxFiles);
      }

      if (files.length > 0) {
        onFilesSelected(files);
      }
    },
    [disabled, acceptedTypes, maxFiles, onFilesSelected],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragOver(true);
      }
    },
    [disabled],
  );

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const handleClick = useCallback(() => {
    if (!disabled) {
      inputRef.current?.click();
    }
  }, [disabled]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
      // Reset input value so the same file can be selected again
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [handleFiles],
  );

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      className={`relative flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-12 transition-colors ${
        disabled
          ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-50 dark:border-gray-700 dark:bg-gray-800/50'
          : isDragOver
            ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20'
            : 'border-gray-300 bg-white hover:border-gray-400 dark:border-gray-600 dark:bg-gray-900 dark:hover:border-gray-500'
      }`}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label="File drop zone. Click or drag files to upload."
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className={`mb-4 rounded-full p-3 ${
        isDragOver
          ? 'bg-blue-100 dark:bg-blue-900/30'
          : 'bg-gray-100 dark:bg-gray-800'
      }`}>
        <UploadCloud
          className={`h-8 w-8 ${
            isDragOver
              ? 'text-blue-500 dark:text-blue-400'
              : 'text-gray-400 dark:text-gray-500'
          }`}
        />
      </div>

      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
        {isDragOver ? 'Drop files here' : 'Drag and drop files here'}
      </p>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        or
      </p>
      <div className="mt-2 inline-flex items-center rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
        Browse Files
        {maxFiles ? ` (max ${maxFiles})` : ''}
      </div>

      {accept && (
        <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
          Accepted: {accept}
        </p>
      )}

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        multiple={!maxFiles || maxFiles > 1}
        onChange={handleInputChange}
        disabled={disabled}
        tabIndex={-1}
      />
    </div>
  );
}
